import {
  ReadSearchApiArg,
  ReadSearchApiResponse,
  SearchScope,
} from 'lib-search-types';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { krakenApi } from '../../kraken.api';

import {
  createInitialState,
  useLazyReadSearchInfiniteQueryReducer,
} from './useLazyReadSearchInfiniteQuery.reducer';
import {
  ReadSearchInfiniteQueryOptions,
  ReadSearchInfiniteQueryTrigger,
  ReadSearchInfiniteQueryTriggerOptions,
  UseLazyReadSearchInfiniteQueryReturn,
} from './useLazyReadSearchInfiniteQuery.types';

/**
 * A hook to call the Search API with infinite scrolling/pagination support,
 * modelled after RTK Query's `useInfiniteQuery`.
 *
 * This is the lazy variant, which does not automatically trigger a search on
 * mount. You must call the returned `trigger` function to initiate a search.
 *
 * Returns a `[trigger, result]` tuple, similar to RTK Query's `useLazyQuery`.
 *
 * @example
 *   ```tsx
 *   const [readSearchUsers, { results, isFetching, hasNextPage, fetchNextPage }] =
 *     useLazyReadSearchInfiniteQuery<'users'>();
 *
 *   readSearchUsers({ scope: 'users', query: 'jane' }, { debounceTime: 300 });
 *   ```;
 *
 * @see {@link useReadSearchInfiniteQuery} for the eager variant.
 */
export const useLazyReadSearchInfiniteQuery = <
  TScope extends SearchScope,
  TEnriched extends boolean = false,
>({
  infinite = true,
  sortOnSearch = false,
}: ReadSearchInfiniteQueryOptions = {}): UseLazyReadSearchInfiniteQueryReturn<
  TScope,
  TEnriched
> => {
  type Arg = ReadSearchApiArg<TScope, TEnriched>;
  type Response = ReadSearchApiResponse<TScope, TEnriched>;
  type Result = Response['data']['results'][number]['result'];

  const dispatch = useAppDispatch();
  const { initiate } = krakenApi.endpoints.readSearch;

  // Single reducer for all query-related state.
  const [state, localDispatch] = useReducer(
    useLazyReadSearchInfiniteQueryReducer<Result, Response['meta']>,
    undefined,
    createInitialState<Result, Response['meta']>,
  );

  // Internal state for trigger/debounce.
  const timeoutRef = useRef<number>();
  const debounceTimeRef = useRef(0);
  const argRef = useRef<Arg>();
  const [arg, setArg] = useState<Arg>();
  const [debouncedArg, setDebouncedArg] = useState<Arg>();

  /** Fetches results from the search endpoint. */
  const fetchResults = useCallback(
    async ({
      fetchArg,
      direction,
    }: {
      fetchArg: Arg;
      direction?: 'next' | 'previous';
    }) => {
      localDispatch({ type: 'fetchStarted', direction });

      // Strip sortBy when searching by query, unless sortOnSearch is enabled.
      const effectiveArg =
        !sortOnSearch && fetchArg.query
          ? ({ ...fetchArg, sortBy: undefined } as typeof fetchArg)
          : fetchArg;

      const response = dispatch(
        (initiate as unknown as typeof initiate)(effectiveArg as never, {
          forceRefetch: true,
        }),
      );
      response.unsubscribe();

      const { data: responseData } = await response;

      if (responseData) {
        const { data, meta } = responseData;
        const results = ((data as Response['data'])?.results ?? []).map(
          ({ result }) => result,
        );

        localDispatch({
          type: 'fetchSucceeded',
          results,
          meta: meta as Response['meta'],
          infinite,
          direction,
        });
      } else {
        localDispatch({ type: 'fetchFailed' });
      }
    },
    [initiate, dispatch, infinite, sortOnSearch],
  );

  /**
   * Trigger a new search. Replaces the current arg entirely. Supports an
   * optional `debounceTime` for delayed fetching.
   */
  const trigger = useCallback<
    ReadSearchInfiniteQueryTrigger<TScope, TEnriched>
  >(
    (
      newArg,
      { debounceTime = 0 }: ReadSearchInfiniteQueryTriggerOptions = {},
    ) => {
      debounceTimeRef.current = debounceTime;
      argRef.current = newArg;
      setArg(newArg);
    },
    [],
  );

  const page = state.meta?.page ?? 0;
  const totalPages = state.meta?.total_pages ?? 0;

  /** Fetch the next page of results. */
  const fetchNextPage = useCallback(() => {
    if (page + 1 >= totalPages) return;

    const currentArg = argRef.current;
    if (!currentArg) return;

    const nextArg = { ...currentArg, page: page + 1 } as Arg;
    argRef.current = nextArg;
    setDebouncedArg(nextArg);

    fetchResults({ fetchArg: nextArg, direction: 'next' });
  }, [fetchResults, page, totalPages]);

  /** Fetch the previous page of results. */
  const fetchPreviousPage = useCallback(() => {
    if (page <= 0) return;

    const currentArg = argRef.current;
    if (!currentArg) return;

    const prevArg = { ...currentArg, page: page - 1 } as Arg;
    argRef.current = prevArg;
    setDebouncedArg(prevArg);

    fetchResults({ fetchArg: prevArg, direction: 'previous' });
  }, [fetchResults, page]);

  /** Re-fetch with the current arg. */
  const refetch = useCallback(() => {
    const currentArg = argRef.current;
    if (!currentArg) return;

    fetchResults({ fetchArg: currentArg });
  }, [fetchResults]);

  /**
   * Optimistically update the local results using an Immer-style recipe. Mutate
   * the draft array in place — the hook produces the next immutable state.
   */
  const updateResults = useCallback((recipe: (draft: Result[]) => void) => {
    localDispatch({ type: 'updateResults', recipe });
  }, []);

  // Fetch when arg changes (via trigger).
  useEffect(() => {
    if (arg) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      const update = () => {
        fetchResults({ fetchArg: arg });
        setDebouncedArg(arg);
      };

      if (debounceTimeRef.current > 0) {
        timeoutRef.current = window.setTimeout(update, debounceTimeRef.current);
      } else {
        update();
      }
    }
    // Excluding `fetchResults` from the dependencies array to prevent an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arg]);

  // Derive RTK Query-style status booleans.
  const isUninitialized = state.asyncStatus === 'uninitialized';
  const isFetching = state.asyncStatus === 'pending';
  const isLoading = isFetching && !state.results.length;
  const isSuccess = state.asyncStatus === 'fulfilled';
  const isError = state.asyncStatus === 'rejected';

  return [
    trigger,
    {
      // Data.
      results: state.results,
      meta: state.meta,
      originalArgs: debouncedArg,

      // Status flags (RTK Query).
      isUninitialized,
      isLoading,
      isFetching,
      isSuccess,
      isError,

      // Pagination (RTK Query infiniteQuery).
      hasNextPage: page + 1 < totalPages,
      hasPreviousPage: page > 0,
      fetchNextPage,
      fetchPreviousPage,
      isFetchingNextPage: state.isFetchingNextPage,
      isFetchingPreviousPage: state.isFetchingPreviousPage,

      // Re-fetch (RTK Query).
      refetch,

      // Local mutations.
      updateResults,
    },
  ] as UseLazyReadSearchInfiniteQueryReturn<TScope, TEnriched>;
};
